import React, { useRef, useEffect } from 'react'
import { Box } from '@material-ui/core'
import useStyles from './styles'

// Video.js CSS is loaded via CDN in the component
const VIDEOJS_CSS_URL = 'https://vjs.zencdn.net/8.10.0/video-js.css'
const VIDEOJS_JS_URL = 'https://vjs.zencdn.net/8.10.0/video.min.js'

const VideoPlayer = ({ src, poster, onClose, autoplay = true }) => {
  const classes = useStyles()
  const videoRef = useRef(null)
  const playerRef = useRef(null)
  const containerRef = useRef(null)

  useEffect(() => {
    // Load Video.js CSS
    if (!document.querySelector(`link[href="${VIDEOJS_CSS_URL}"]`)) {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = VIDEOJS_CSS_URL
      document.head.appendChild(link)
    }

    // Load Video.js script
    const loadVideoJS = () => {
      return new Promise((resolve, reject) => {
        if (window.videojs) {
          resolve(window.videojs)
          return
        }

        if (document.querySelector(`script[src="${VIDEOJS_JS_URL}"]`)) {
          // Script is loading, wait for it
          const checkInterval = setInterval(() => {
            if (window.videojs) {
              clearInterval(checkInterval)
              resolve(window.videojs)
            }
          }, 100)
          return
        }

        const script = document.createElement('script')
        script.src = VIDEOJS_JS_URL
        script.async = true
        script.onload = () => resolve(window.videojs)
        script.onerror = reject
        document.body.appendChild(script)
      })
    }

    const initPlayer = async () => {
      try {
        const videojs = await loadVideoJS()

        if (!videoRef.current) return

        // Initialize Video.js player
        const player = videojs(videoRef.current, {
          controls: true,
          autoplay: autoplay,
          preload: 'auto',
          fluid: true,
          responsive: true,
          playbackRates: [0.5, 1, 1.5, 2],
          controlBar: {
            volumePanel: {
              inline: false,
            },
            pictureInPictureToggle: false,
          },
          sources: [
            {
              src: src,
              type: getVideoType(src),
            },
          ],
          poster: poster,
        })

        playerRef.current = player

        // Handle player errors
        player.on('error', () => {
          console.error('Video.js error:', player.error())
        })

        // Handle keyboard shortcuts
        player.on('keydown', e => {
          if (e.key === 'Escape' && onClose) {
            onClose()
          }
        })
      } catch (error) {
        console.error('Failed to initialize Video.js:', error)
      }
    }

    initPlayer()

    // Cleanup
    return () => {
      if (playerRef.current) {
        playerRef.current.dispose()
        playerRef.current = null
      }
    }
  }, [src, poster, autoplay, onClose])

  const getVideoType = url => {
    if (!url) return 'video/mp4'
    const lowerUrl = url.toLowerCase()
    if (lowerUrl.includes('.webm')) return 'video/webm'
    if (lowerUrl.includes('.mov')) return 'video/quicktime'
    if (lowerUrl.includes('.m4v')) return 'video/mp4'
    return 'video/mp4'
  }

  return (
    <Box className={classes.root} ref={containerRef}>
      <Box data-vjs-player className={classes.playerWrapper}>
        <video
          ref={videoRef}
          className="video-js vjs-big-play-centered vjs-theme-forest"
          playsInline
        />
      </Box>
    </Box>
  )
}

export default VideoPlayer
